/**
 * TaxonomyAdminPage (S1.1) — 產品分類 Product Categories admin.
 *
 * Left: TaxonomyTreeEditor (expand/collapse, type-to-filter, drag
 * reorder/reparent with depth-cap toast, inline add-child, inactive gray).
 * Right: NodeDetailPane. 「層級名稱 Level Names」modal = the rename facility
 * for the 5 bilingual level labels + L4/L5 enablement.
 *
 * Node selection is route-derived (`?node=` search param) so deep links
 * restore the pane. Data: /api/taxonomy/* cacheKeys via useTaxonomy hooks;
 * every mutation clears those caches before the page refreshes the tree.
 */

import { useMemo, useState } from 'react';
import { Button, Space, Spin, Switch, Typography, theme } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageContainer from '@/components/common/PageContainer';
import PageHeader from '@/components/common/PageHeader';
import SearchInput from '@/components/common/SearchInput';
import { ListErrorState } from '@/components/common/TableContainer';
import TaxonomyTreeEditor from '@/components/common/TaxonomyTreeEditor';
import type { TaxonomyMoveIntent } from '@/components/common/TaxonomyTreeEditor';
import CreateNodeModal from './CreateNodeModal';
import LevelNamesModal from './LevelNamesModal';
import MoveCommentModal from './MoveCommentModal';
import NodeDetailPane from './NodeDetailPane';
import { useCategoryLevels, useTaxonomyTree } from '@/hooks/useTaxonomy';
import { useTaxonomyStore } from '@/stores/taxonomyStore';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles';

const { Text } = Typography;

export default function TaxonomyAdminPage() {
  const { t } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();

  const { nodes, error, isLoading, refresh: refreshTree } = useTaxonomyTree();
  const { levels, refresh: refreshLevels } = useCategoryLevels();
  const { filters, setSearch, setIncludeInactive } = useTaxonomyStore();

  // Route-derived selection (`?node=`)
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('node');
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedId) ?? null,
    [nodes, selectedId]
  );

  // `createParentId` stays null-only now — CreateNodeModal is kept ONLY for
  // "Add top-level category" (no parent row to anchor a popover to). Add-subcategory
  // routes through the anchored quick-create popover instead (OQ-2).
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeQuickCreateParentId, setActiveQuickCreateParentId] = useState<string | null>(null);
  const [levelNamesOpen, setLevelNamesOpen] = useState(false);
  const [moveIntent, setMoveIntent] = useState<TaxonomyMoveIntent | null>(null);

  const selectNode = (id: string) => {
    setSearchParams({ node: id });
  };

  // Wave-2 elevation applied to the resting admin panels (shadow-border card).
  const panelStyle: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    padding: token.paddingLG,
    ...elevation.card,
  };

  const refreshAll = () => {
    refreshTree();
    refreshLevels();
  };

  return (
    <PageContainer>
      <PageHeader
        title={t('taxonomy:title')}
        extra={
          <Space size="middle">
            <Button
              icon={<SettingOutlined />}
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => setLevelNamesOpen(true)}
            >
              {t('taxonomy:levelNames')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => {
                setCreateParentId(null);
                setCreateOpen(true);
              }}
            >
              {t('taxonomy:addRoot')}
            </Button>
          </Space>
        }
      />

      {/* Toolbar: type-to-filter + include-inactive */}
      <div className="tw-mb-6 tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
        <SearchInput
          value={filters.search}
          onChange={setSearch}
          placeholder={t('taxonomy:tree.searchPlaceholder')}
          width="min(420px, 100%)"
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          <Switch
            checked={filters.includeInactive}
            onChange={setIncludeInactive}
            aria-label={t('taxonomy:tree.includeInactive')}
          />
          <Text type="secondary">{t('taxonomy:tree.includeInactive')}</Text>
        </div>
      </div>

      {error ? (
        <div style={panelStyle}>
          <ListErrorState
            title={t('taxonomy:tree.loadFailed')}
            retryLabel={t('taxonomy:tree.retry')}
            onRetry={refreshTree}
          />
        </div>
      ) : (
        <div className="tw-flex tw-gap-6 tw-items-start tw-flex-wrap">
          {/* Left: tree editor */}
          <div
            style={{
              ...panelStyle,
              flex: '1 1 420px',
              minWidth: 280,
              maxHeight: 'calc(100vh - 320px)',
              overflowY: 'auto',
            }}
          >
            {isLoading && nodes.length === 0 ? (
              <div className="tw-flex tw-justify-center tw-py-16">
                <Spin size="large" />
              </div>
            ) : (
              <TaxonomyTreeEditor
                nodes={nodes}
                levels={levels}
                selectedId={selectedId}
                onSelect={selectNode}
                search={filters.search}
                includeInactive={filters.includeInactive}
                onAddChild={(parentId) => setActiveQuickCreateParentId(parentId)}
                activeQuickCreateParentId={activeQuickCreateParentId}
                onQuickCreated={(newNodeId, keepOpen) => {
                  // Both save modes refresh the tree so a fresh sibling shows and the
                  // next code suggestion sees it. Save (keepOpen=false) also selects
                  // the new node and closes; Save & add another keeps the popover open.
                  refreshTree();
                  if (!keepOpen) {
                    setActiveQuickCreateParentId(null);
                    selectNode(newNodeId);
                  }
                }}
                onQuickCreateCancel={() => setActiveQuickCreateParentId(null)}
                onMove={setMoveIntent}
              />
            )}
          </div>

          {/* Right: detail pane */}
          <div style={{ ...panelStyle, flex: '1 1 480px', minWidth: 280 }}>
            <NodeDetailPane
              node={selectedNode}
              nodes={nodes}
              levels={levels}
              onChanged={refreshTree}
            />
          </div>
        </div>
      )}

      <CreateNodeModal
        open={createOpen}
        parentId={createParentId}
        nodes={nodes}
        onClose={() => setCreateOpen(false)}
        onCreated={(newNodeId) => {
          refreshTree();
          selectNode(newNodeId);
        }}
      />

      <LevelNamesModal
        open={levelNamesOpen}
        levels={levels}
        onClose={() => setLevelNamesOpen(false)}
        onSaved={refreshAll}
      />

      <MoveCommentModal
        intent={moveIntent}
        nodes={nodes}
        onClose={() => setMoveIntent(null)}
        onMoved={refreshTree}
      />
    </PageContainer>
  );
}
